<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  META_WHATSAPP_PRICE_USD,
  campaignCostUsd,
} from 'dashboard/constants/campaigns';

const props = defineProps({
  engagement: { type: Object, default: () => ({}) },
  totalRecipientsCount: { type: Number, default: 0 },
});

const { t } = useI18n();

const contacted = computed(() => props.engagement.contacted || 0);

// The denominator for every rate is what was actually contacted, not what was targeted: a contact
// the campaign never reached cannot have failed to read it.
const rate = value =>
  contacted.value ? Math.round((value * 100) / contacted.value) : null;

const cost = computed(() =>
  campaignCostUsd(props.engagement.pricing || {}, META_WHATSAPP_PRICE_USD)
);

const formattedCost = computed(() =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: cost.value < 1 ? 4 : 2,
    maximumFractionDigits: cost.value < 1 ? 4 : 2,
  }).format(cost.value)
);

// `rate: null` is spelled out on the two steps that have none: leaving the key off makes it
// `undefined`, which is not `=== null`, and the template then prints "(undefined%)".
const steps = computed(() => [
  { key: 'TARGETS', value: props.totalRecipientsCount, rate: null },
  { key: 'CONTACTED', value: contacted.value, rate: null },
  {
    key: 'DELIVERED',
    value: props.engagement.delivered || 0,
    rate: rate(props.engagement.delivered || 0),
  },
  {
    key: 'READ',
    value: props.engagement.read || 0,
    rate: rate(props.engagement.read || 0),
    hint: 'READ_HINT',
  },
  {
    key: 'REPLIED',
    value: props.engagement.replied || 0,
    rate: rate(props.engagement.replied || 0),
    hint: 'REPLIED_HINT',
    emphasis: true,
  },
]);

const hasEngagement = computed(() => contacted.value > 0);
</script>

<template>
  <div
    v-if="hasEngagement"
    class="flex flex-wrap items-start gap-x-6 gap-y-2 pt-3 mt-3 border-t border-n-weak"
  >
    <div
      v-for="step in steps"
      :key="step.key"
      class="flex flex-col"
      :title="step.hint ? t(`CAMPAIGN.FUNNEL.${step.hint}`) : null"
    >
      <span
        class="text-base font-medium tabular-nums"
        :class="
          step.emphasis && step.value ? 'text-n-teal-11' : 'text-n-slate-12'
        "
      >
        {{ step.rate === null ? step.value : `${step.value} (${step.rate}%)` }}
      </span>
      <span class="text-xs uppercase tracking-wide text-n-slate-11">
        {{ t(`CAMPAIGN.FUNNEL.${step.key}`) }}
      </span>
    </div>

    <div
      v-if="cost > 0"
      class="flex flex-col"
      :title="t('CAMPAIGN.FUNNEL.COST_HINT')"
    >
      <span class="text-base font-medium text-n-slate-12 tabular-nums">
        {{ formattedCost }}
      </span>
      <span class="text-xs uppercase tracking-wide text-n-slate-11">
        {{ t('CAMPAIGN.FUNNEL.COST') }}
      </span>
    </div>
  </div>
</template>
